import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Button from '@mui/material/Button';
import CloudIcon from '@mui/icons-material/Cloud';
import CodeIcon from '@mui/icons-material/Code';
import LanIcon from '@mui/icons-material/Lan';
import VerifiedIcon from '@mui/icons-material/Verified';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import { Link } from 'react-router-dom';

import AnimationContainer from '../components/AnimationContainer';
import CustomButton from '../components/CustomButton';
import PageGrid from '../components/PageGrid';
import PageLead from '../components/PageLead';
import PageSection from '../components/PageSection';
import ProjectModal from '../components/ProjectModal';
import References from '../components/References';
import Separator from '../components/Separator';
import Seo from '../components/Seo';
import ShowCase from '../components/ShowCase';
import WorkDiagram from '../components/WorkDiagram';
import { useAppContext } from '../hooks/AppProvider';
import { trackEvent } from '../modules/matomo';

const Home = () => {
  const [projectModalOpen, setProjectModalOpen] = React.useState(false);
  const { reducedMotion } = useAppContext();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  return <>
    <Seo
      title="IT, Hosting & Software, die im Alltag funktionieren | mk mediaconcept GbR"
      description="Individuelle Software, zuverlässiges Hosting und pragmatische IT-Betreuung für Unternehmen, die sich auf ihre Technik verlassen müssen."
      canonical="https://www.mymk.de/"
    />
    <PageSection>
      <Separator />
      <AnimationContainer lazy={() => import('../components/three/PointWaves')} />
      <PageLead>
        <h1>IT, Hosting & Software,<br />die im Alltag funktionieren.</h1>
        <p>Wir entwickeln nicht nur individuelle Anwendungen, sondern betreiben auch die Systeme dahinter. Stabil, verständlich und ohne unnötigen Overhead.</p>
        <p>Seit über 30 Jahren vertrauen uns Unternehmen aus unterschiedlichsten Branchen.</p>
      </PageLead>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
          gap: theme.spacing(4),
        },
      }}>
        <CustomButton
          className="xxl"
          startIcon={<AssignmentIcon />}
          onClick={() => {
            setProjectModalOpen(true);
            trackEvent('Projektanfrage', 'Dialog Öffnen', 'Hero Home');
          }}
        >
          Projekt anfragen
        </CustomButton>
        <CustomButton
          href="#leistungen"
          onClick={(e) => {
            e.preventDefault();
            const target = document.querySelector('#leistungen');
            if (target) {
              target.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth' });
              trackEvent('Leistungen', 'Scroll into View', 'Hero Home');
            }
          }}
        >
          Leistungen ansehen
        </CustomButton>
      </Box>
      <Separator height={mobile ? '45px' : '120px'} type="triangle-left-slant-down" position="bottom" />
      <Separator fill="services" height={mobile ? '25px' : '100px'} type="triangle-left-slant-down" position="bottom" />
    </PageSection>
    <PageSection bg="grayGradient" sx={{ display: 'block' }}>
      <h2 id="leistungen">
        Leistungen
      </h2>
      <p>
        Was wir für Sie tun können
      </p>
      <PageGrid container spacing={{ xs: 4, lg: 8 }}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <LanIcon className="icon" />
          <Box className="title">
            Netzwerk & IT-Betreuung
          </Box>
          <span>Stabile IT-Infrastruktur, Betreuung und Sicherheitschecks.</span>
          <List>
            <ListItem>
              <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
              <ListItemText>Betreuung von Servern, Netzwerken und Arbeitsplätzen</ListItemText>
            </ListItem>
            <ListItem>
              <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
              <ListItemText>Sicherheitsanalysen und Optimierung</ListItemText>
            </ListItem>
            <ListItem>
              <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
              <ListItemText>Schnelle Hilfe per Remote oder vor Ort</ListItemText>
            </ListItem>
          </List>
          <CustomButton
            aria-label="Mehr Erfahren über Netzwerk & IT-Betreuung"
            component={Link}
            endIcon={<ArrowRightAltIcon />}
            fullWidth
            to="/administration"
            sx={{
              marginTop: '2rem',
              padding: theme => theme.spacing(1, 0),
              background: theme => theme.palette.background.transparent,
            }}
            title="Mehr Erfahren über Netzwerk & IT-Betreuung"
          >
            Mehr Erfahren
          </CustomButton>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <CodeIcon className="icon" sx={{ fontSize: '30vw !important', transform: 'rotate(0deg) !important' }} />
          <Box className="title">
            Applikationsentwicklung
          </Box>
          <span>Individuelle Webanwendungen, Schnittstellen und interne Tools.</span>
          <List>
            <ListItem>
              <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
              <ListItemText>Maßgeschneiderte Software statt Standardlösungen</ListItemText>
            </ListItem>
            <ListItem>
              <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
              <ListItemText>Integration in bestehende Systeme</ListItemText>
            </ListItem>
            <ListItem>
              <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
              <ListItemText>Wartbar, skalierbar und langfristig nutzbar</ListItemText>
            </ListItem>
          </List>
          <CustomButton
            aria-label="Mehr Erfahren über Applikationsentwicklung"
            component={Link}
            endIcon={<ArrowRightAltIcon />}
            fullWidth
            to="/software"
            sx={{
              marginTop: '2rem',
              padding: theme => theme.spacing(1, 0),
              background: theme => theme.palette.background.transparent,
            }}
            title="Mehr Erfahren über Applikationsentwicklung"
          >
            Mehr Erfahren
          </CustomButton>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <CloudIcon className="icon" />
          <Box className="title">
            Hosting & E-Mail
          </Box>
          <span>Zuverlässiges Hosting für Websites, Anwendungen und Kommunikation.</span>
          <List>
            <ListItem>
              <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
              <ListItemText>Performantes Hosting & Serverlösungen</ListItemText>
            </ListItem>
            <ListItem>
              <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
              <ListItemText>E-Mail-Systeme und Groupware</ListItemText>
            </ListItem>
            <ListItem>
              <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
              <ListItemText>Betrieb, Monitoring und Support aus einer Hand</ListItemText>
            </ListItem>
          </List>
          <CustomButton
            aria-label="Mehr Erfahren über Hosting & E-Mail"
            component={Link}
            endIcon={<ArrowRightAltIcon />}
            fullWidth
            to="/hosting"
            sx={{
              marginTop: '2rem',
              padding: theme => theme.spacing(1, 0),
              background: theme => theme.palette.background.transparent,
            }}
            title="Mehr Erfahren über Hosting & E-Mail"
          >
            Mehr Erfahren
          </CustomButton>
        </Grid>
      </PageGrid>
    </PageSection>
    <PageSection bg="grayGreenGradient">
      <Box className="content">
        <h2>
          Referenzen
        </h2>
        <p>
          <strong>Unternehmen, die uns vertrauen</strong>
          <br />
          Seit vielen Jahren betreuen wir kleine und mittelständische Unternehmen bei ihren IT-Projekten – zuverlässig, pragmatisch und auf Augenhöhe.
        </p>
        <References>
          <Box><img src="/assets/img/logos/wiesbaden.svg" alt="Landeshauptstadt Wiesbaden" /></Box>
          <Box><img src="/assets/img/logos/eswe.svg" alt="ESWE Versorgungs AG" /></Box>
          <Box><img src="/assets/img/logos/witcom.svg" alt="Witcom GmbH" /></Box>
          <Box><img src="/assets/img/logos/seg.svg" alt="SEG GmbH" /></Box>
          <Box><img src="/assets/img/logos/wibau.svg" alt="WiBau GmbH" /></Box>
          <Box><img src="/assets/img/logos/gwi.png" alt="GWI GmbH" /></Box>
          <Box><img src="/assets/img/logos/magentatv.svg" alt="MagentaTV" /></Box>
          <Box><img src="/assets/img/logos/evim.svg" alt="Evim GmbH" /></Box>
          <Box><img src="/assets/img/logos/rippon-boswell-wiesbaden.svg" alt="Rippon Boswell & Co." /></Box>
          <Box><img src="/assets/img/logos/medimobil.png" alt="Medimobil GmbH" /></Box>
          <Box><img src="/assets/img/logos/sal.png" alt="SAL GmbH" /></Box>
          <Box><img src="/assets/img/logos/industrialport.png" alt="IndustrialPort GmbH" /></Box>
          <Box><img src="/assets/img/logos/awi.svg" alt="Alfred-Wegener-Institut" /></Box>
          <Box><img src="/assets/img/logos/zmt.svg" alt="Leibniz Zentrum für Marine Tropenforschung" /></Box>
          <Box><img src="/assets/img/logos/biermannneff.png" alt="BiermannNeff GmbH" /></Box>
          <Box><img src="/assets/img/logos/aulba.svg" alt="Aulba Immobilien GmbH" /></Box>
          <Box><img src="/assets/img/logos/aldiana.svg" alt="Aldiana GmbH" /></Box>
          <Box><img src="/assets/img/logos/herz-kreislauf-praxis.png" alt="Herz-Kreislauf-Praxis Wiesbaden" /></Box>
          <Box><img src="/assets/img/logos/vc-wiesbaden.webp" alt="VC Wiesbaden" /></Box>
          <Box><img src="/assets/img/logos/viertler.gif" alt="Viertler GmbH" /></Box>
          <Box><img src="/assets/img/logos/ahrens.jpg" alt="Ahrens GmbH" /></Box>
          <Box><img src="/assets/img/logos/zillmann.svg" alt="Zillmann GmbH" /></Box>
          <Box><img src="/assets/img/logos/kdz.svg" alt="KDZ GmbH" /></Box>
          <Box><img src="/assets/img/logos/haese.svg" alt="Autohaus Haese" /></Box>
          <Box className="action"><Button onClick={() => {
            setProjectModalOpen(true);
            trackEvent('Projektanfrage', 'Dialog Öffnen', 'Referenzen Home');
          }}>Kunde werden</Button></Box>
        </References>
        <Box sx={{
          mt: '15vw',
          textAlign: 'center',
          '& > p': {
            fontSize: 'max(1.5vw, 12pt)',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          },
        }}>
          <p>Projekte aus der Praxis</p>
          <ShowCase />
        </Box>
      </Box>
    </PageSection>
    <PageSection bg="greenPinkGradient">
      <Box>
        <img src={`/assets/img/karikatur-ben-small${theme.palette.mode === 'dark' ? '-kontur' : ''}.png`} alt="" style={{ maxWidth: '300px' }} />
      </Box>
      <Box className="content">
        <h2>
          Warum Kunden mit uns arbeiten
        </h2>
        <p>
          <strong>Direkt & unkompliziert</strong>
          <br />
          Kein unnötiger Overhead – Sie sprechen direkt mit den Kollegen, die es umsetzen.
        </p>
        <p>
          <strong>Über 30 Jahre Erfahrung</strong>
          <br />
          Nicht nur Technik, sondern auch die typischen Probleme im Alltag verstanden.
        </p>
        <p>
          <strong>Individuelle Lösungen</strong>
          <br />
          Keine Standardprodukte – wir bauen, was wirklich gebraucht wird.
        </p>
        <p>
          <strong>Zuverlässig im Betrieb</strong>
          <br />
          Wir denken nicht nur bis zum Launch, sondern darüber hinaus.
        </p>
      </Box>
    </PageSection>
    <PageSection bg="pinkGradient">
      <Box className="content left">
        <h2>
          Lösungen, die zu Ihrem Unternehmen passen
        </h2>
        <p>
          Jedes Unternehmen arbeitet anders. Deshalb entwickeln wir keine Lösungen von der Stange, sondern Systeme, die zu Ihren Abläufen passen.
        </p>
        <p>
          Technisch sinnvoll, wirtschaftlich tragfähig und langfristig betreibbar.
        </p>
      </Box>
      <img src={`/assets/img/lollie-${theme.palette.mode}.png`} alt="" style={{ maxWidth: '500px', width: '100%' }} />
      <Separator fill="requirements" type="triangle-left-slant-down" position="bottom" />
    </PageSection>
    <PageSection bg="paper">
      <Box className="content">
        <WorkDiagram />
      </Box>
      <Separator fill="project" type="triangle-right-slant-up" position="bottom" />
    </PageSection>
    <PageSection bg="blueGreenGradient">
      <Box className="content center">
        <h2>
          Lassen Sie uns über<br />Ihr Projekt sprechen
        </h2>
        <p>
          Sie haben eine Idee oder stehen vor einer konkreten Herausforderung?
        </p>
        <p>
          Wir schauen uns das gemeinsam an,
          <br/>
          flexibel und unverbindlich.
        </p>
        <CustomButton
          onClick={() => {
            setProjectModalOpen(true);
            trackEvent('Projektanfrage', 'Dialog Öffnen', 'Call to Action Home');
          }}
          startIcon={<AssignmentIcon />}
          sx={{
            background: theme.palette.background.paper,
            marginTop: '2rem',
          }}
        >
          Projekt anfragen
        </CustomButton>
      </Box>
    </PageSection>
    <ProjectModal open={projectModalOpen} onClose={() => setProjectModalOpen(false)} />
  </>;
};

export const Component = Home;

export { default as ErrorBoundary } from '../components/ErrorMessage';

export default Home;
